import { ExternalLink, FileText, Lock, Paperclip, Send, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { fetchFile } from '../lib/api';
import type { AttachmentPolicy, TicketAttachment, TicketMessage, TicketStatus, TicketSummary, TicketType } from '../lib/types';

// Help & Feedback tickets (#414): pieces shared by the parent's page, the Student Portal and Admin → Support.

export const TICKET_TYPES: { id: TicketType; label: string; hint: string }[] = [
  { id: 'question', label: 'Question', hint: 'How do I…?' },
  { id: 'problem', label: 'Something’s not working', hint: 'A problem or error' },
  { id: 'idea', label: 'Idea', hint: 'Something you’d like ClassGuardian to do' },
  { id: 'account', label: 'Account & plan', hint: 'Sign-in, sharing or your plan' },
];
export const typeLabel = (t: TicketType) => TICKET_TYPES.find((x) => x.id === t)?.label ?? t;

/** Status words: the family sees "Waiting on you" where an admin sees "Waiting on parent". */
export function statusLabel(s: TicketStatus, forAdmin = false) {
  switch (s) {
    case 'open':
      return forAdmin ? 'Open' : 'Waiting for support';
    case 'in_progress':
      return 'In progress';
    case 'waiting':
      return forAdmin ? 'Waiting on parent' : 'Waiting on you';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
  }
}

export function StatusTag({ status, forAdmin = false }: { status: TicketStatus; forAdmin?: boolean }) {
  return <span className={`tag ticket-status ${status}`}>{statusLabel(status, forAdmin)}</span>;
}

const when = (ms: number) =>
  new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** One row in a list of tickets. */
export function TicketRow({ t, onOpen, forAdmin = false, extra }: { t: TicketSummary; onOpen: () => void; forAdmin?: boolean; extra?: React.ReactNode }) {
  return (
    <button className={`ticket-row ${t.unread ? 'unread' : ''}`} onClick={onOpen}>
      {t.unread && <span className="unread-dot" aria-label="New reply" />}
      <span className="ticket-row-main">
        <strong>{t.subject}</strong>
        <span className="muted small">
          #{t.number} · {typeLabel(t.type)} · {t.openedBy.kind === 'student' ? `${t.openedBy.name} (student)` : t.openedBy.name} · updated {when(t.updatedAt)}
        </span>
      </span>
      {extra}
      <StatusTag status={t.status} forAdmin={forAdmin} />
    </button>
  );
}

// --- Attachments (#446) ---------------------------------------------------------------------------------

/** A file ready to send: its name and contents (base64). */
export type Upload = { name: string; data: string };
type Picked = Upload & { size: number };

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function readFile(f: File): Promise<Picked> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ name: f.name, size: f.size, data: String(r.result).replace(/^data:[^,]*,/, '') });
    r.onerror = () => reject(new Error(`Couldn’t read ${f.name}.`));
    r.readAsDataURL(f);
  });
}

/** Attach screenshots (and PDFs, if allowed) to a message, within the admin's limits. */
export function AttachPicker({ policy, files, onChange, disabled }: { policy?: AttachmentPolicy | null; files: Picked[]; onChange: (f: Picked[]) => void; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  if (!policy?.enabled) return null;
  const accept = [...IMAGE_TYPES, ...(policy.pdf ? ['application/pdf'] : [])];
  async function add(list: FileList | null) {
    setError('');
    const next = [...files];
    for (const f of Array.from(list ?? [])) {
      if (next.length >= policy!.perMessage) {
        setError(`Up to ${policy!.perMessage} file${policy!.perMessage === 1 ? '' : 's'} per message.`);
        break;
      }
      if (!accept.includes(f.type)) {
        setError(`${f.name} isn’t a picture${policy!.pdf ? ' or a PDF' : ''}.`);
        continue;
      }
      if (f.size > policy!.maxMb * 1024 * 1024) {
        setError(`${f.name} is too big (up to ${policy!.maxMb} MB).`);
        continue;
      }
      try {
        next.push(await readFile(f));
      } catch (e) {
        setError((e as Error).message);
      }
    }
    onChange(next);
    if (input.current) input.current.value = '';
  }
  return (
    <div className="attach-picker">
      <input ref={input} type="file" accept={accept.join(',')} multiple hidden onChange={(e) => void add(e.target.files)} />
      <button type="button" className="btn small" disabled={disabled || files.length >= policy.perMessage} onClick={() => input.current?.click()}>
        <Paperclip size={14} /> {policy.pdf ? 'Attach a screenshot or PDF' : 'Attach a screenshot'}
      </button>
      {files.map((f, i) => (
        <span key={`${f.name}-${i}`} className="attach-chip">
          {f.name} <span className="muted">({kb(f.size)})</span>
          <button type="button" className="icon-btn" aria-label={`Remove ${f.name}`} onClick={() => onChange(files.filter((_, j) => j !== i))}>
            <X size={13} />
          </button>
        </span>
      ))}
      {error && <span className="bad-text small">{error}</span>}
    </div>
  );
}

/** A request's files: pictures as thumbnails (click to open), PDFs to download. They're fetched with the sign-in. */
function Attachments({ files, base, who }: { files: TicketAttachment[]; base: string; who: 'parent' | 'student' }) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    let live = true;
    const made: string[] = [];
    for (const f of files.filter((x) => x.mime.startsWith('image/'))) {
      fetchFile(`${base}/${f.id}`, who)
        .then((b) => {
          const u = URL.createObjectURL(b);
          made.push(u);
          if (live) setUrls((cur) => ({ ...cur, [f.id]: u }));
        })
        .catch(() => undefined);
    }
    return () => {
      live = false;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files, base, who]);
  async function open(f: TicketAttachment) {
    const blob = await fetchFile(`${base}/${f.id}`, who);
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    if (!f.mime.startsWith('image/')) a.download = f.name;
    else a.target = '_blank';
    a.click();
    setTimeout(() => URL.revokeObjectURL(u), 60_000);
  }
  return (
    <div className="ticket-files">
      {files.map((f) =>
        f.mime.startsWith('image/') ? (
          <button key={f.id} className="ticket-thumb" onClick={() => void open(f)} title={`${f.name} (${kb(f.size)})`}>
            {urls[f.id] ? <img src={urls[f.id]} alt={f.name} /> : <span className="muted small">{f.name}</span>}
          </button>
        ) : (
          <button key={f.id} className="btn small" onClick={() => void open(f)}>
            <FileText size={14} /> {f.name} <span className="muted">({kb(f.size)})</span>
          </button>
        ),
      )}
    </div>
  );
}

/** The conversation. Private notes (admins only) are marked; system lines are small. */
export function TicketThread({
  messages,
  issue,
  files = '/api/support/attachments',
  who = 'parent',
}: {
  messages: TicketMessage[];
  issue?: { number: number | null; url?: string };
  /** Where this audience downloads attachments from (#446). */
  files?: string;
  who?: 'parent' | 'student';
}) {
  return (
    <ol className="ticket-thread">
      {messages.map((m) =>
        m.author.kind === 'system' ? (
          <li key={m.id} className="ticket-msg system muted small">
            {m.internal && <Lock size={12} />} {m.body} · {when(m.createdAt)}
          </li>
        ) : (
          <li key={m.id} className={`ticket-msg ${m.author.kind === 'admin' ? 'from-support' : 'from-family'} ${m.internal ? 'internal' : ''}`}>
            <div className="ticket-msg-head small">
              <strong>{m.author.kind === 'admin' ? `${m.author.name} · ClassGuardian support` : m.author.name}</strong>
              {m.internal && (
                <span className="tag">
                  <Lock size={11} /> Private note
                </span>
              )}
              <span className="muted">{when(m.createdAt)}</span>
            </div>
            <p className="pre">{m.body}</p>
            {!!m.attachments?.length && <Attachments files={m.attachments} base={files} who={who} />}
          </li>
        ),
      )}
      {issue?.number && (
        <li className="ticket-msg system small">
          With the developers
          {issue.url ? (
            <>
              {' '}
              as{' '}
              <a href={issue.url} target="_blank" rel="noreferrer">
                GitHub issue #{issue.number} <ExternalLink size={12} />
              </a>
            </>
          ) : (
            ` (issue #${issue.number})`
          )}
          .
        </li>
      )}
    </ol>
  );
}

/** Write a reply, with screenshots if the server allows them (#446). */
export function ReplyBox({
  onSend,
  placeholder = 'Write a reply…',
  label = 'Send',
  disabled,
  attachments,
}: {
  onSend: (text: string, files: Upload[]) => Promise<unknown>;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  attachments?: AttachmentPolicy | null;
}) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<Picked[]>([]);
  const [busy, setBusy] = useState(false);
  async function send() {
    setBusy(true);
    try {
      const ok = await onSend(text.trim(), files.map(({ name, data }) => ({ name, data })));
      if (ok !== false) {
        setText('');
        setFiles([]);
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack ticket-reply">
      <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} maxLength={10000} aria-label="Reply" disabled={disabled} />
      <AttachPicker policy={attachments} files={files} onChange={setFiles} disabled={disabled || busy} />
      <div>
        <button className="btn primary" disabled={busy || disabled || !text.trim()} onClick={() => void send()}>
          <Send size={15} /> {busy ? 'Sending…' : label}
        </button>
      </div>
    </div>
  );
}

/** Open a new ticket: the kind, a subject and the details. */
export function NewTicketForm({
  onSubmit,
  submitLabel = 'Send',
  allowAccount = true,
  allowed,
  attachments,
}: {
  onSubmit: (t: { type: TicketType; subject: string; body: string; attachments: Upload[] }) => Promise<unknown>;
  submitLabel?: string;
  allowAccount?: boolean;
  allowed?: TicketType[];
  attachments?: AttachmentPolicy | null;
}) {
  const offered = TICKET_TYPES.filter((t) => (allowAccount || t.id !== 'account') && (!allowed || allowed.includes(t.id)));
  const [type, setType] = useState<TicketType>(offered[0]?.id ?? 'question');
  // The admin's allowed types can arrive after the first render: keep the choice on one of them.
  useEffect(() => {
    if (offered.length && !offered.some((t) => t.id === type)) setType(offered[0]!.id);
  }, [offered, type]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<Picked[]>([]);
  const [busy, setBusy] = useState(false);
  const types = offered;
  async function submit() {
    setBusy(true);
    try {
      await onSubmit({ type, subject: subject.trim(), body: body.trim(), attachments: files.map(({ name, data }) => ({ name, data })) });
      setSubject('');
      setBody('');
      setFiles([]);
    } finally {
      setBusy(false);
    }
  }
  const hint = TICKET_TYPES.find((t) => t.id === type)!;
  return (
    <div className="stack">
      <div className="segmented wrap" role="radiogroup" aria-label="What is it about?">
        {types.map((t) => (
          <button key={t.id} role="radio" aria-checked={type === t.id} className={type === t.id ? 'active' : ''} onClick={() => setType(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <label>
        Subject
        <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150} placeholder={hint.hint} />
      </label>
      <label>
        {type === 'problem' ? 'What were you doing, and what happened?' : type === 'idea' ? 'How would it help?' : 'Details'}
        <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} />
      </label>
      <AttachPicker policy={attachments} files={files} onChange={setFiles} disabled={busy} />
      <div>
        <button className="btn primary" disabled={busy || subject.trim().length < 3 || body.trim().length < 10} onClick={() => void submit()}>
          {busy ? 'Sending…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
